import React from 'react'
import { imageUrl } from '../lib/images'
import SeoHead from '../components/SeoHead'
import WaveDivider from '../components/WaveDivider'
import { localizeTree } from '../i18n/localizeTree'

export default function FindMigPage({ lang = 'da' }) {
  return localizeTree((
    <>
      <SeoHead pageId="contact" lang="da" />

      {/* Header Banner */}
      <section className="subpage-hero bg-[#F7ECEE] py-12 sm:py-16 border-b border-[#C68D94]/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-script text-5xl sm:text-6xl text-[#3E3435] font-normal my-3">
            Kom bare forbi!
          </h1>
          <WaveDivider className="text-[#C68D94]/50 mx-auto mb-6" />
          <h2 className="text-base sm:text-lg font-medium text-[#3E3435] mb-2">
            Min cafe og værksted finder du her:
          </h2>
          <p className="text-sm sm:text-base text-[#A35D67] font-semibold">
            <a
              href="https://www.google.com/maps/place//data=!4m2!3m1!1s0x464b27403c6c11b1:0xdba83c7efc8a154a?sa=X&ved=1t:8290&ictx=111"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:underline inline-flex items-center gap-1.5"
            >
              <span>Strandvejen 58 • 6720 Fanø</span>
              <i className="fa-solid fa-arrow-up-right-from-square text-xs" aria-hidden="true" />
            </a>
          </p>
        </div>
      </section>

      {/* Main Content Grid: Booking/Form & Details */}
      <section className="py-14 sm:py-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          {/* Left Column: Her finder du mig & Opening Hours */}
          <div className="lg:col-span-6 space-y-10">
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#A35D67]">
                Ved Fanø Bad
              </span>
              <h2 className="font-script text-4xl sm:text-5xl text-[#3E3435] font-normal my-2">
                Her finder du mig
              </h2>
              <WaveDivider className="text-[#C68D94]/50 mb-6" />

              <div className="text-xs sm:text-sm text-[#3E3435]/85 font-light leading-relaxed space-y-4">
                <p>
                  Min café og butik ligger midt på <strong>Strandvejen 58 ved Fanø Bad</strong> – kun et stenkast fra stranden og det pulserende liv i området. Her kan du kigge ind, nyde en kop kaffe eller te sammen med chokolade, kage eller sæsonens små fristelser – og selvfølgelig tage dine favoritter med hjem.
                </p>
                <p>
                  Uanset om du er på ferie på Fanø eller bare på en lille udflugt, er du altid velkommen til at kigge forbi mit chokoladeunivers.
                </p>
              </div>

              <div className="mt-6 flex flex-wrap gap-4 pt-2">
                <a
                  href="tel:60224704"
                  className="bg-[#C68D94] hover:bg-[#A35D67] text-white px-6 py-3 rounded-full text-xs font-semibold uppercase tracking-wider transition-colors shadow-sm whitespace-nowrap inline-flex items-center gap-2"
                >
                  <i className="fa-solid fa-phone" aria-hidden="true" />
                  <span>Ring: 60 22 47 04</span>
                </a>
                <a
                  href="mailto:karina@fanoechokolade.dk"
                  className="border border-[#C68D94]/40 hover:bg-[#F7ECEE] text-[#3E3435] px-6 py-3 rounded-full text-xs font-semibold uppercase tracking-wider transition-colors whitespace-nowrap inline-flex items-center gap-2"
                >
                  <span>karina@fanoechokolade.dk</span>
                </a>
              </div>
            </div>

            {/* Opening Hours Table */}
            <div className="pt-2">
              <div className="flex items-center gap-2 mb-4">
                <i className="fa-regular fa-clock text-[#A35D67]" aria-hidden="true" />
                <h3 className="font-semibold text-base text-[#3E3435] uppercase tracking-wider">
                  Åbningstider
                </h3>
              </div>
              <div className="divide-y divide-stone-100 text-xs sm:text-sm">
                <div className="py-2.5 flex justify-between">
                  <span className="font-medium text-[#3E3435]">Mandag</span>
                  <span className="text-stone-600">10:00 – 17:00</span>
                </div>
                <div className="py-2.5 flex justify-between">
                  <span className="font-medium text-[#3E3435]">Tirsdag</span>
                  <span className="text-stone-600">10:00 – 17:00</span>
                </div>
                <div className="py-2.5 flex justify-between">
                  <span className="font-medium text-[#3E3435]">Onsdag</span>
                  <span className="text-stone-600">10:00 – 17:00</span>
                </div>
                <div className="py-2.5 flex justify-between">
                  <span className="font-medium text-[#3E3435]">Torsdag</span>
                  <span className="text-stone-600">10:00 – 17:00</span>
                </div>
                <div className="py-2.5 flex justify-between">
                  <span className="font-medium text-[#3E3435]">Fredag</span>
                  <span className="text-stone-600">10:00 – 17:00</span>
                </div>
                <div className="py-2.5 flex justify-between">
                  <span className="font-medium text-[#3E3435]">Lørdag</span>
                  <span className="text-stone-600">10:00 – 17:00</span>
                </div>
                <div className="py-2.5 flex justify-between font-medium text-[#A35D67]">
                  <span>Søndag</span>
                  <span>Lukket</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Overlapping Circular Photography */}
          <div className="lg:col-span-6 relative flex justify-center items-center py-6 lg:py-8">
            {/* Primary Large Circle */}
            <div className="aspect-square w-[18rem] sm:w-[24rem] md:w-[28rem] lg:w-[32rem] xl:w-[36rem] rounded-full overflow-hidden border-4 border-white shadow-xl bg-stone-100 relative z-10 shrink-0">
              <img
                src={imageUrl(
                  '/_assets/imgi-106-515206049-767625565591366-2309087864801483466-n.jpeg',
                  'width=1400,height=1400,fit=cover,quality=85,format=auto'
                )}
                alt="Udendørs caféterrasse med borde, parasoller og gæster tæt ved klitterne"
                className="w-full h-full object-cover"
              />
            </div>

            {/* Secondary Overlapping Small Circle */}
            <div className="absolute -bottom-6 -left-4 sm:-bottom-2 sm:-left-2 lg:bottom-4 lg:-left-4 aspect-square w-36 sm:w-48 md:w-56 lg:w-64 xl:w-72 rounded-full overflow-hidden border-4 border-white shadow-lg bg-stone-100 z-20 shrink-0">
              <img
                src={imageUrl(
                  '/_assets/imgi-2-548634743-827060689647853-6046308657816260637-n.jpeg',
                  'width=800,height=800,fit=cover,quality=85,format=auto'
                )}
                alt="Udendørs terrasse ved café med store parasoller, glasafskærmning og gæster"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Café Galleri Sektion */}
      <section className="pb-16 sm:pb-20 md:pb-24 bg-[#FDF8F7]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-center">
            {/* Foto 1 */}
            <div className="rounded-t-full rounded-b-3xl overflow-hidden aspect-[4/3] shadow-sm border border-[#C68D94]/20 group">
              <img
                src={imageUrl(
                  '/_assets/fanoechokolade-098-e1758706985886.jpg',
                  'width=600,height=450,fit=cover,quality=80,format=auto'
                )}
                alt="Fanø Chokolade café foto"
                className="w-full h-full object-cover transition-transform duration-500 "
              />
            </div>

            {/* Foto 2 */}
            <div className="rounded-t-full rounded-b-3xl overflow-hidden aspect-[4/3] shadow-sm border border-[#C68D94]/20 group">
              <img
                src={imageUrl(
                  '/_assets/fanoechokolade-106-scaled-e1757667485907.jpg',
                  'width=600,height=450,fit=cover,quality=80,format=auto'
                )}
                alt="Caféstuen"
                className="w-full h-full object-cover transition-transform duration-500 "
              />
            </div>

            {/* Foto 3 */}
            <div className="rounded-t-full rounded-b-3xl overflow-hidden aspect-[4/3] shadow-sm border border-[#C68D94]/20 group">
              <img
                src={imageUrl(
                  '/_assets/fanoechokolade-057-scaled-e1758707017625.jpg',
                  'width=600,height=450,fit=cover,quality=80,format=auto'
                )}
                alt="Udenfor caféen"
                className="w-full h-full object-cover transition-transform duration-500 "
              />
            </div>
          </div>
        </div>
      </section>
    </>
  ), lang)
}
